import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { unifiedApiService } from '../../services/unified';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export function Breadcrumbs() {
  const location = useLocation();
  const [jobTitle, setJobTitle] = useState<string | null>(null);
  
  // Fetch job title if we're on a job details page
  useEffect(() => {
    const pathMatch = location.pathname.match(/^\/jobs\/([a-f0-9-]+)$/);
    if (pathMatch) {
      const jobId = pathMatch[1];
      unifiedApiService.jobs.getJobById(jobId)
        .then(job => setJobTitle(job.title))
        .catch(() => setJobTitle(null));
    } else {
      setJobTitle(null);
    }
  }, [location.pathname]);
  
  // Generate breadcrumbs from current path
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', href: '/' }
    ];

    // Map path segments to readable labels
    const pathLabels: Record<string, string> = {
      'dashboard': 'Dashboard',
      'jobs': 'Jobs',
      'profile': 'Profile',
      'admin': 'Admin Panel',
      'settings': 'Settings',
      'applications': 'Applications',
      'resumes': 'Resumes',
      'messages': 'Messages',
      'about': 'About Us',
      'contact': 'Contact',
      'login': 'Sign In',
      'privacy': 'Privacy Policy',
      'terms': 'Terms of Service',
    };

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Use job title if available and this is a job ID segment
      let label = pathLabels[segment];
      if (!label) {
        // Check if this looks like a UUID (job ID)
        if (/^[a-f0-9-]{36}$/.test(segment) && jobTitle) {
          label = jobTitle;
        } else {
          label = segment.charAt(0).toUpperCase() + segment.slice(1);
        }
      }
      
      // Don't add href for the last item (current page)
      const isLast = index === pathSegments.length - 1;
      breadcrumbs.push({
        label,
        href: isLast ? undefined : currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show anything for home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {breadcrumbs.map((crumb, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <svg
                className="h-4 w-4 text-gray-400 mx-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ width: '1rem', height: '1rem', maxWidth: '1rem', maxHeight: '1rem' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            {crumb.href ? (
              <Link
                to={crumb.href}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="text-sm font-medium text-gray-900">
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}